<template>
  <section
    class="project-group"
    :class="{
      'project-group-archived': group.isArchived,
      'project-group--editing': editingEventId !== null,
    }"
  >
    <header
      class="project-group-heading"
      :class="{ 'project-group-heading-static': !collapsible }"
      :role="collapsible ? 'button' : null"
      :tabindex="collapsible ? 0 : null"
      @click="collapsible && $emit('toggle-project')"
      @keydown.enter.self="collapsible && $emit('toggle-project')"
      @keydown.space.self.prevent="collapsible && $emit('toggle-project')"
    >
      <div class="project-group-heading-left">
        <span class="project-group-name">{{ group.projectName }}</span>
        <span v-if="group.isArchived" class="archived-badge">Archived</span>
      </div>
      <div class="project-group-heading-right">
        <b-button
          v-if="canCreateTimesheet"
          size="sm"
          variant="outline-primary"
          class="add-hours-btn"
          @click.stop="$emit('bulk-hours', { group })"
        >
          + Add Hours
        </b-button>
        <b-button
          v-if="canEditTimesheet && stats.pendingCount > 0"
          size="sm"
          variant="success"
          class="approve-all-group-btn"
          :disabled="approvingAll"
          @click.stop="$emit('approve-all-group', group)"
        >
          <b-spinner v-if="approvingAll" small class="mr-1" />
          Approve all pending
        </b-button>
        <span
          v-if="stats.pendingCount > 0"
          class="status-pill status-pill-sm status-pending"
        >
          {{ stats.pendingCount }} pending
        </span>
        <span class="project-group-stat">
          <b-icon-people-fill />
          {{ stats.count }} workers
        </span>
        <span class="project-group-stat project-group-stat--hours">
          <b-icon-clock />
          {{ stats.totalHours }} total hours
        </span>
        <b-icon-chevron-right
          v-if="collapsible"
          class="expand-icon"
          :class="{ expanded: !collapsed }"
        />
      </div>
    </header>

    <template v-if="!collapsed">
      <div v-if="hasVisibleRows" class="roster-group">
        <timesheet-roster-header />
        <tradie-row
          v-for="row in activeTradies"
          :key="row.tradieId"
          :row="row"
          :row-key="row.tradieId + '-' + group.projectId"
          :project-id="group.projectId"
          :week-days="weekDays"
          :selected-day="selectedDayFn(row.tradieId + '-' + group.projectId)"
          :is-expanded="isExpandedFn(row.tradieId + '-' + group.projectId)"
          :can-create-timesheet="canCreateTimesheet"
          :can-edit-timesheet="canEditTimesheet"
          :can-delete-timesheet="canDeleteTimesheet"
          :is-supervisor-tradie="isSupervisorTradie"
          :show-no-entries="showNoEntries"
          :editing-event-id="editingEventId"
          :edit-form="editForm"
          :max-future-date="maxFutureDate"
          :approving-all="approvingAll"
          @toggle="$emit('row-toggle', $event)"
          @approve-all="$emit('approve-all', $event)"
          @clock-out="$emit('clock-out', $event)"
          @day-click="$emit('day-click', $event)"
          @view-event="$emit('view-event', $event)"
          @approve="forwardApprove"
          @reject="$emit('reject', $event)"
          @edit="$emit('edit', $event)"
          @save-edit="$emit('save-edit', $event)"
          @cancel-edit="$emit('cancel-edit')"
          @delete="$emit('delete', $event)"
          @edit-leave="$emit('edit-leave', $event)"
          @delete-leave="$emit('delete-leave', $event)"
          @lightbox="forwardLightbox"
        />
      </div>

      <inactive-tradie-section
        v-if="group.inactiveTradies.length > 0 && showInactiveRows"
        :tradies="group.inactiveTradies"
        :collapsed="inactiveCollapsed"
        :can-create-timesheet="canCreateTimesheet"
        :can-edit-timesheet="canEditTimesheet"
        :can-delete-timesheet="canDeleteTimesheet"
        :is-supervisor-tradie="isSupervisorTradie"
        :project-id="group.projectId"
        :week-days="weekDays"
        :is-expanded-fn="isExpandedFn"
        :selected-day-fn="selectedDayFn"
        :editing-event-id="editingEventId"
        :edit-form="editForm"
        :max-future-date="maxFutureDate"
        @toggle="$emit('toggle-inactive')"
        @row-toggle="$emit('row-toggle', $event)"
        @day-click="$emit('day-click', $event)"
        @view-event="$emit('view-event', $event)"
        @approve="forwardApprove"
        @reject="$emit('reject', $event)"
        @edit="$emit('edit', $event)"
        @save-edit="$emit('save-edit', $event)"
        @cancel-edit="$emit('cancel-edit')"
        @delete="$emit('delete', $event)"
        @edit-leave="$emit('edit-leave', $event)"
        @delete-leave="$emit('delete-leave', $event)"
        @lightbox="forwardLightbox"
      />
    </template>
  </section>
</template>

<script>
import InactiveTradieSection from '@/components/timesheets/list/InactiveTradieSection.vue'
import TimesheetRosterHeader from '@/components/timesheets/list/TimesheetRosterHeader.vue'
import TradieRow from '@/components/timesheets/list/TradieRow.vue'
import { rosterStats } from '@/utils/timesheetRosterPresentation'

export default {
  name: 'TimesheetProjectGroup',

  components: {
    InactiveTradieSection,
    TimesheetRosterHeader,
    TradieRow,
  },

  props: {
    group: { type: Object, required: true },
    collapsed: { type: Boolean, default: false },
    collapsible: { type: Boolean, default: true },
    inactiveCollapsed: { type: Boolean, default: true },
    showInactiveRows: { type: Boolean, default: true },
    showNoEntries: { type: Boolean, default: false },
    visibleTradiesFn: { type: Function, required: true },
    isExpandedFn: { type: Function, required: true },
    selectedDayFn: { type: Function, required: true },
    weekDays: { type: Array, default: () => [] },
    canCreateTimesheet: { type: Boolean, default: false },
    canEditTimesheet: { type: Boolean, default: false },
    canDeleteTimesheet: { type: Boolean, default: false },
    isSupervisorTradie: { type: Boolean, default: false },
    editingEventId: { type: [Number, String], default: null },
    editForm: { type: Object, default: null },
    maxFutureDate: { type: String, default: '' },
    approvingAll: { type: Boolean, default: false },
  },

  computed: {
    activeTradies() {
      return this.visibleTradiesFn(this.group.tradies)
    },
    stats() {
      return rosterStats(this.group.tradies)
    },
    hasVisibleRows() {
      return (
        this.activeTradies.length > 0 ||
        (this.showInactiveRows && this.group.inactiveTradies.length > 0)
      )
    },
  },

  methods: {
    forwardApprove(eventId, decision) {
      this.$emit('approve', eventId, decision)
    },
    forwardLightbox(urls, index) {
      this.$emit('lightbox', urls, index)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.project-group {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  overflow: hidden;

  // The edit form's date picker popup must not be clipped by the card
  &.project-group--editing {
    overflow: visible;
  }
}

.project-group-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  cursor: pointer;
  user-select: none;
}

.project-group-heading-static {
  cursor: default;
}

.project-group-heading-left,
.project-group-heading-right,
.project-group-stat {
  display: flex;
  align-items: center;
}

.project-group-heading-left {
  gap: 8px;
  min-width: 0;
}

.project-group-heading-right {
  gap: 20px;
  flex-shrink: 0;
}

.project-group-stat {
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.add-hours-btn {
  flex-shrink: 0;
}

.project-group-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.01em;
}

.project-group-archived .project-group-name {
  color: #9ca3af;
}

.archived-badge {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  padding: 1px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.expand-icon {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: transform 0.2s;

  &.expanded {
    transform: rotate(90deg);
  }
}

.roster-group {
  border-top: 1px solid #f1f5f9;
}

@media (max-width: 575.98px) {
  .project-group-heading {
    align-items: flex-start;
    padding: 14px;
  }

  .project-group-heading-right {
    gap: 10px;
  }

  .project-group-stat {
    font-size: 12px;
  }

  .project-group-stat--hours {
    display: none;
  }
}
</style>
